import {
  ColumnFormatAgentSchema,
  ColumnFormatSchema,
  coerceColumnFormat,
} from "@repo/shared/types/column-format";
import { z } from "zod";

import { SortTypeSchema } from "../types/sort";
import { OperationTypeSchema } from "./operation-types";

/** Delimiter used in aggregated column keys (e.g. "price::SUM") */
export const AGG_DELIMITER = "::";

export const LogicOperatorSchema = z.enum(["AND", "OR"]);
export type LogicOperatorType = z.infer<typeof LogicOperatorSchema>;

export const FilterModeSchema = z.enum([
  "MATCHES",
  "CONTAINS",
  "NOT_CONTAINS",
  "EMPTY",
  "NOT_EMPTY",
  "EQUAL",
  "NOT_EQUAL",
  "ENDS",
  "STARTS",
  "GREATER",
  "GREATER_EQUAL",
  "LESS",
  "LESS_EQUAL",
  "IS_FALSE",
  "IS_TRUE",
  "RELATIVE",
  "BETWEEN",
  "IN",
  "NOT_IN",
]);
export type FilterModeType = z.infer<typeof FilterModeSchema>;

export const RelativeModeSchema = z.enum(["PREVIOUS", "CURRENT", "NEXT"]);
export type RelativeModeType = z.infer<typeof RelativeModeSchema>;

export const RelativeIntervalSchema = z.enum([
  "HOUR",
  "DAY",
  "WEEK",
  "MONTH",
  "QUARTER",
  "YEAR",
]);
export type RelativeIntervalType = z.infer<typeof RelativeIntervalSchema>;

export const FilterRelativeSchema = z.object({
  mode: RelativeModeSchema,
  interval: RelativeIntervalSchema,
  value: z.number(),
});
export type FilterRelative = z.infer<typeof FilterRelativeSchema>;

/** Value shape for BETWEEN date filter: ISO date strings (yyyy-MM-dd). */
export const DateRangeValueSchema = z.object({
  start: z.string(),
  end: z.string(),
});
export type DateRangeValue = z.infer<typeof DateRangeValueSchema>;

export const FilterTypeSchema = z.object({
  columns: z.array(z.string()).min(1),
  logicOperator: LogicOperatorSchema.nullable(),
  mode: FilterModeSchema,
  relative: FilterRelativeSchema.nullable(),
  value: z.any(),
});
export type FilterType = z.infer<typeof FilterTypeSchema>;

/** Per-column configuration (display name, format, order, visibility, width). */
export const ColumnConfigSchema = z.object({
  displayName: z
    .string()
    .optional()
    .describe("Custom display name for the column header."),
  // The `preprocess` upconverts legacy `{ formatType, formatOptions }` on
  // parse. LEGACY BACK-COMPAT (removable) — once the legacy path is retired
  // (see the removal guide in `packages/shared/types/column-format.ts`), this
  // can collapse to `format: ColumnFormatSchema.optional()`.
  format: z
    .preprocess(
      (raw) => coerceColumnFormat(raw) ?? raw,
      ColumnFormatSchema.optional(),
    )
    .describe(
      "How the column values are displayed. A discriminated union keyed by `kind` (e.g. { kind: 'PERCENT', scale: '0-1' }, { kind: 'CURRENCY', symbol: '€' }, { kind: 'DATE', dateFormat: 'MONTH' }).",
    ),
  order: z
    .string()
    .optional()
    .describe(
      "Fractional-index key for column display order. Columns are sorted lexicographically by this key; columns without a key appear last in their original engine order. Managed by the UI — do not set manually.",
    ),
  visible: z
    .boolean()
    .optional()
    .describe("Whether the column is visible (default: true)."),
  width: z.number().optional().describe("Column width in pixels."),
});
export type ColumnConfig = z.infer<typeof ColumnConfigSchema>;

export const ColumnsConfigSchema = z
  .record(z.string(), ColumnConfigSchema)
  .describe("Per-column display configuration keyed by column name.");
export type ColumnsConfig = z.infer<typeof ColumnsConfigSchema>;

/** Data transformation configuration (grouping, aggregation, filtering, sorting). */
export const RiftConfigSchema = z.object({
  limit: z
    .number()
    .nullable()
    .default(null)
    .describe("Row limit (null for no limit)."),
  groups: z
    .record(z.string().describe("Column accessor"), z.boolean())
    .default({})
    .describe("Columns to group by. Set column name to true to group."),
  aggregates: z
    .record(
      z.string().describe("Column accessor"),
      z
        .array(OperationTypeSchema)
        .describe("Operations to perform on the column"),
    )
    .default({})
    .describe("Aggregation operations per column. Always pair with groups."),
  filters: z
    .array(FilterTypeSchema)
    .default([])
    .describe("Row filter conditions."),
  sorts: z
    .array(
      z.object({
        column: z.string().describe("Column accessor"),
        direction: SortTypeSchema,
      }),
    )
    .default([])
    .describe("Sort order for the result rows."),
  footers: z
    .record(z.string().describe("Column accessor"), OperationTypeSchema)
    .default({})
    .describe("Footer aggregation per column (shown below table)."),
  hiddenColumns: z
    .array(z.string())
    .default([])
    .describe("Column names to hide from the visualization."),
});

export type RiftConfig = z.infer<typeof RiftConfigSchema>;

/** A complete RiftConfig with all fields set to their empty/null defaults. */
export const DEFAULT_RIFT_CONFIG: RiftConfig = {
  limit: null,
  groups: {},
  aggregates: {},
  filters: [],
  sorts: [],
  footers: {},
  hiddenColumns: [],
};

// ---------------------------------------------------------------------------
// Agent-facing slim variants
//
// Anthropic's strict structured-output / tool grammar caps the number of
// omittable properties (anything not in `required[]`) at 24 per request.
// Zod `.optional()` and `.default(...)` both produce omittable properties.
// To stay under the cap, agent-facing schemas use these slim variants:
//
//   - every property is required (no `.default()` / `.optional()` at the
//     boundary the model sees)
//   - genuinely nullable fields use `.nullable()` so the model emits an
//     explicit `null` instead of omitting them
//   - the `filters` field is dropped entirely — `FilterType.value` is
//     `z.any()`, which Anthropic's GA structured-output endpoint sanitizes
//     to `{}` and rejects. Filters are restored from existing state (or set
//     to `[]` on create) by the wrapper before persisting.
// ---------------------------------------------------------------------------

/** Per-column display configuration, slim variant for AI agent emission. */
export const ColumnConfigAgentSchema = z.object({
  displayName: z
    .string()
    .nullable()
    .describe(
      "Custom display name for the column header. Use null to keep the default.",
    ),
  format: ColumnFormatAgentSchema.nullable().describe(
    "How the column values display: a discriminated union keyed by `kind`. Examples: { kind: 'PERCENT', scale: '0-1' }, { kind: 'CURRENCY', symbol: '€' }, { kind: 'DATE', dateFormat: 'MONTH' }. Use null to keep the default.",
  ),
  visible: z
    .boolean()
    .nullable()
    .describe(
      "Whether the column is visible. Use null to keep the default (visible).",
    ),
});
export type ColumnConfigAgent = z.infer<typeof ColumnConfigAgentSchema>;

export const ColumnsConfigAgentSchema = z
  .record(z.string(), ColumnConfigAgentSchema)
  .describe(
    "Per-column display configuration keyed by projected column name. Emit `{}` to keep defaults for every column.",
  );
export type ColumnsConfigAgent = z.infer<typeof ColumnsConfigAgentSchema>;

/**
 * RiftConfig variant exposed to the AI agent. Drops `filters` (see note
 * above) and removes every `.default(...)` so the schema has zero optional
 * properties. The wrapper merges `filters` back from the existing block on
 * update (or sets `[]` on create) before persisting.
 */
export const RiftConfigAgentSchema = z.object({
  limit: z.number().nullable().describe("Row limit (null for no limit)."),
  groups: z
    .record(z.string().describe("Column accessor"), z.boolean())
    .describe(
      "Columns to group by. Emit `{}` for no grouping; set a column name to true to group by it.",
    ),
  aggregates: z
    .record(
      z.string().describe("Column accessor"),
      z
        .array(OperationTypeSchema)
        .describe("Operations to perform on the column"),
    )
    .describe(
      "Aggregation operations per column. Emit `{}` when there are no aggregates.",
    ),
  sorts: z
    .array(
      z.object({
        column: z.string().describe("Column accessor"),
        direction: SortTypeSchema,
      }),
    )
    .describe("Sort order for the result rows. Emit `[]` for no sorting."),
  footers: z
    .record(z.string().describe("Column accessor"), OperationTypeSchema)
    .describe(
      "Footer aggregation per column (shown below table). Emit `{}` for none.",
    ),
  hiddenColumns: z
    .array(z.string())
    .describe(
      "Column names to hide from the visualization. Emit `[]` to hide none.",
    ),
});
export type RiftConfigAgent = z.infer<typeof RiftConfigAgentSchema>;
